/**
 * One-click presets for common manual predictions, with base-rate-derived
 * multiplier suggestions.
 *
 * RATIONALE FOR MULTIPLIERS
 * --------------------------
 * Manual predictions have a flat cost (coins) and flat bonus multiplier (× base
 * points). To keep the leaderboard impact roughly proportional to risk, we tune
 * the suggested multiplier inversely to the prediction's base rate (probability):
 *
 *   suggested_multiplier ≈ 1 / probability  (with sensible bounds)
 *
 * E.g. a 25% likely event suggests ≈ 4.0×; a 50% likely event suggests ≈ 2.0×.
 *
 * Probabilities below are approximate, derived from publicly-reported FIFA
 * tournament data (last several World Cups, men's). Refine with your own
 * historical data once you have it.
 *
 * Sources/notes for base rates (approx, men's WC):
 *   - Group-stage match drawn:                 ~28%   (≈4 of 14 group games per round)
 *   - Knockout match goes to extra time:       ~25%   (≈1 in 4 KO matches)
 *   - Knockout match decided by penalties:     ~12%   (≈1 in 8 KO matches)
 *   - Both teams to score (any match):         ~50%
 *   - Combined goals 4+ (any match):           ~20%
 *   - Combined goals 3+ (any match):           ~45%
 *   - 1+ red card (any match):                 ~15%
 *   - Specific team scores 2+ goals (avg):     ~35%   (high variance by team)
 */

export interface ManualPropPreset {
  /** Stable key for selection */
  key: string;
  /** Short label shown on the preset button */
  label: string;
  /** One-line context shown next to the label */
  hint: string;
  /** Approximate base rate (0–1). Surfaced in the modal so admin can sanity-check. */
  baseRate: number;
  /** Suggested cost in coins (flat) */
  suggestedCostCoins: number;
  /** Suggested bonus multiplier — inversely proportional to baseRate, rounded sensibly */
  suggestedMultiplier: number;
  /** What admin should pick: 'fan-out' presets duplicate across games via Custom + applyToAllGames */
  fanOutEligible: boolean;
  /** Description text used as customDescription. Same wording is duplicated per game. */
  description: string;
  /** Which round phase this preset is most relevant for. Hint only — doesn't gate selection. */
  contextHint: 'group-stage' | 'knockout' | 'any';
}

export const MANUAL_PROP_PRESETS: ManualPropPreset[] = [
  {
    key: 'match-draw',
    label: 'Match ends in a draw',
    hint: 'Group stage only — knockouts decide via ET/PK',
    baseRate: 0.28,
    suggestedCostCoins: 75,
    suggestedMultiplier: 3.5,
    fanOutEligible: true,
    description: 'Will this match end in a draw?',
    contextHint: 'group-stage',
  },
  {
    key: 'extra-time-or-penalties',
    label: 'Match goes to extra time or penalties',
    hint: 'Knockout only — base rate ~25%',
    baseRate: 0.25,
    suggestedCostCoins: 75,
    suggestedMultiplier: 4.0,
    fanOutEligible: true,
    description: 'Will this match go to extra time or penalties?',
    contextHint: 'knockout',
  },
  {
    key: 'penalties-only',
    label: 'Match decided by penalty shootout',
    hint: 'Knockout only — base rate ~12%',
    baseRate: 0.12,
    suggestedCostCoins: 50,
    suggestedMultiplier: 8.0,
    fanOutEligible: true,
    description: 'Will this match be decided by a penalty shootout?',
    contextHint: 'knockout',
  },
  {
    key: 'both-teams-to-score',
    label: 'Both teams to score',
    hint: 'Classic soccer prop — base rate ~50%',
    baseRate: 0.5,
    suggestedCostCoins: 100,
    suggestedMultiplier: 2.0,
    fanOutEligible: true,
    description: 'Will both teams score in this match?',
    contextHint: 'any',
  },
];

export function findPreset(key: string): ManualPropPreset | undefined {
  return MANUAL_PROP_PRESETS.find((p) => p.key === key);
}
